/**
 * รับพนักงานใหม่ — ติดตามความคืบหน้าเช็คลิสต์ onboarding รายคน (auto-tick จากระบบ +
 * ติ๊กมือ) + เริ่มรับพนักงานใหม่ + ทวงพนักงาน + จบการรับเข้า.
 */
import { useMemo, useState } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useToast } from '../../components/ui/toast';
import { useConsequence } from '../../components/ui/ConsequencePreview';
import { Modal } from '../../components/ui/Modal';
import {
  useOnboardingOverview,
  useOnboardingDetail,
  useMarkOnboardingItem,
  useStartOnboarding,
  useCompleteOnboarding,
  useRemindOnboarding,
  useEmployees,
  type OnboardingDetailItem,
} from '../../hooks';
import { ONBOARDING_CATEGORY_LABELS, type OnboardingCategory } from '@payroll/shared';
import { UserPlus, Bell, CheckCircle2, Circle, ChevronRight } from 'lucide-react';

interface EmployeeBasic {
  emp_code: string;
  first_name: string | null;
  last_name: string | null;
  nickname: string | null;
  status: string;
}

function fullName(e: EmployeeBasic): string {
  return `${e.first_name ?? ''} ${e.last_name ?? ''}`.trim() || e.nickname || e.emp_code;
}

export function OwnerOnboarding() {
  const { data: overview, isLoading } = useOnboardingOverview();
  const { data: allEmployees } = useEmployees() as { data: EmployeeBasic[] | undefined };
  const toast = useToast();

  const [selectedEmp, setSelectedEmp] = useState<string | null>(null);
  const [showStartPicker, setShowStartPicker] = useState(false);

  const employees = overview?.employees ?? [];
  const activeCodes = useMemo(() => new Set(employees.map((e) => e.emp_code)), [employees]);
  // รวม pending_start (รอยืนยันตัวตน/รอเริ่มงาน) ด้วย — onboarding = เก็บเอกสาร/ตั้งค่า
  // ก่อนเริ่มงานจริง จึงควรเริ่มรับเข้าได้ตั้งแต่ยังไม่ยืนยันตัวตน. ตัด ลาออก/เลิกจ้าง ออก.
  const startCandidates = (allEmployees ?? []).filter(
    (e) => ['active', 'probation', 'pending_start'].includes(e.status) && !activeCodes.has(e.emp_code),
  );

  return (
    <AdminLayout title="รับพนักงานใหม่">
      <p className="text-sm text-gray-600 mb-3">
        ติดตามเช็คลิสต์รับพนักงานใหม่รายคน — รายการที่ระบบเช็คให้อัตโนมัติ + รายการที่ต้องติ๊กมือ
      </p>

      <div className="flex items-center justify-between mb-3">
        <div className="text-sm text-gray-500">กำลัง onboard {employees.length} คน</div>
        <button onClick={() => setShowStartPicker(true)} className="btn-cta whitespace-nowrap">
          <UserPlus className="w-4 h-4" /> เริ่มรับพนักงานใหม่
        </button>
      </div>

      {isLoading ? (
        <p className="text-sm text-gray-400">กำลังโหลด…</p>
      ) : employees.length === 0 ? (
        <p className="text-sm text-gray-400 italic">ไม่มีพนักงานที่กำลังรับเข้าตอนนี้</p>
      ) : (
        <div className="space-y-2">
          {employees.map((e) => {
            const pct = e.required_count > 0 ? Math.round((e.done_count / e.required_count) * 100) : 100;
            const complete = e.required_count > 0 && e.done_count >= e.required_count;
            return (
              <button
                key={e.emp_code}
                onClick={() => setSelectedEmp(e.emp_code)}
                className="card w-full text-left p-3 flex items-center gap-3 hover:bg-gray-50 transition"
              >
                <div className="flex-1 min-w-0">
                  <div className="font-medium flex items-center gap-2">
                    {e.name}
                    {complete && <span className="text-xs text-green-600 font-semibold">ครบแล้ว</span>}
                  </div>
                  <div className="text-xs text-gray-400 mb-1">
                    {e.emp_code}
                    {e.department ? ` · ${e.department}` : ''}
                  </div>
                  <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${complete ? 'bg-green-500' : 'bg-brand-500'}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <div className="text-[10px] text-gray-400 mt-1">
                    {e.done_count}/{e.required_count} รายการที่จำเป็น
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0" />
              </button>
            );
          })}
        </div>
      )}

      {selectedEmp && (
        <OnboardingDetailModal
          empCode={selectedEmp}
          empName={employees.find((e) => e.emp_code === selectedEmp)?.name ?? selectedEmp}
          onClose={() => setSelectedEmp(null)}
        />
      )}

      {showStartPicker && (
        <StartOnboardingModal
          candidates={startCandidates}
          onClose={() => setShowStartPicker(false)}
          onStarted={(empCode) => {
            setShowStartPicker(false);
            setSelectedEmp(empCode);
          }}
        />
      )}
    </AdminLayout>
  );
}

function StartOnboardingModal({
  candidates,
  onClose,
  onStarted,
}: {
  candidates: EmployeeBasic[];
  onClose: () => void;
  onStarted: (empCode: string) => void;
}) {
  const startM = useStartOnboarding();
  const toast = useToast();
  const [q, setQ] = useState('');
  const filtered = candidates.filter((e) => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return fullName(e).toLowerCase().includes(s) || e.emp_code.toLowerCase().includes(s);
  });

  async function handleStart(empCode: string) {
    try {
      await startM.mutateAsync(empCode);
      toast.success('เริ่มรับพนักงานใหม่แล้ว');
      onStarted(empCode);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'เริ่มไม่สำเร็จ');
    }
  }

  return (
    <Modal open onClose={onClose} title="เลือกพนักงานที่จะเริ่มรับเข้า">
      {candidates.length === 0 ? (
        <p className="text-sm text-gray-400 italic">ไม่มีพนักงานที่รอเริ่ม onboarding (ทุกคนกำลังทำอยู่แล้ว หรือทำเสร็จหมดแล้ว)</p>
      ) : (
        <div className="space-y-2">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="ค้นหาชื่อ / รหัสพนักงาน"
            className="input"
            autoFocus
          />
          <div className="space-y-1 max-h-96 overflow-y-auto">
            {filtered.length === 0 ? (
              <p className="text-sm text-gray-400 italic py-2">ไม่พบพนักงานตามคำค้น</p>
            ) : filtered.map((e) => (
              <button
                key={e.emp_code}
                onClick={() => handleStart(e.emp_code)}
                disabled={startM.isPending}
                className="w-full text-left border border-gray-100 rounded-lg px-3 py-2 text-sm hover:bg-gray-50 flex items-center justify-between"
              >
                <span>
                  {fullName(e)} <span className="text-xs text-gray-400">{e.emp_code}</span>
                </span>
                <ChevronRight className="w-4 h-4 text-gray-300" />
              </button>
            ))}
          </div>
        </div>
      )}
    </Modal>
  );
}

function OnboardingDetailModal({ empCode, empName, onClose }: { empCode: string; empName: string; onClose: () => void }) {
  const { data, isLoading } = useOnboardingDetail(empCode);
  const markM = useMarkOnboardingItem(empCode);
  const completeM = useCompleteOnboarding();
  const remindM = useRemindOnboarding();
  const consequence = useConsequence();
  const toast = useToast();

  const grouped = useMemo(() => {
    const byCat = new Map<OnboardingCategory, OnboardingDetailItem[]>();
    for (const it of data?.items ?? []) {
      const list = byCat.get(it.category) ?? [];
      list.push(it);
      byCat.set(it.category, list);
    }
    return byCat;
  }, [data]);

  async function handleToggle(itemKey: string, done: boolean) {
    try {
      await markM.mutateAsync({ item_key: itemKey, done });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ');
    }
  }

  async function handleRemind() {
    try {
      const r = await remindM.mutateAsync(empCode);
      if (r.skipped === 'no_line') toast.info('พนักงานยังไม่ได้ผูก LINE — ทวงไม่ได้');
      else if (r.skipped === 'no_pending') toast.info('ไม่มีรายการที่ต้องทวงแล้ว');
      else toast.success(`ทวงพนักงานแล้ว (${r.pending_count} รายการ)`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ทวงไม่สำเร็จ');
    }
  }

  async function handleComplete() {
    const ok = await consequence({
      title: 'จบการรับเข้า',
      intro: `ยืนยันว่า ${empName} รับพนักงานใหม่เสร็จสิ้นแล้ว`,
      effects: ['ปิดสถานะ onboarding ของพนักงานคนนี้', 'ไม่แสดงในแดชบอร์ดรับพนักงานใหม่อีก'],
      confirmText: 'จบการรับเข้า',
    });
    if (!ok) return;
    try {
      await completeM.mutateAsync(empCode);
      toast.success('จบการรับเข้าแล้ว');
      onClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ไม่สำเร็จ');
    }
  }

  return (
    <Modal open onClose={onClose} title={empName} size="max-w-2xl">
      <div className="space-y-4">
        <div className="flex gap-2">
          <button onClick={handleRemind} disabled={remindM.isPending} className="btn-secondary flex-1">
            <Bell className="w-4 h-4" /> ทวงพนักงาน
          </button>
          <button onClick={handleComplete} disabled={completeM.isPending} className="btn-cta flex-1">
            <CheckCircle2 className="w-4 h-4" /> จบการรับเข้า
          </button>
        </div>

        {isLoading ? (
          <p className="text-sm text-gray-400">กำลังโหลด…</p>
        ) : (
          [...grouped.entries()].map(([cat, items]) => (
            <div key={cat}>
              <div className="text-xs font-semibold text-gray-500 mb-1">{ONBOARDING_CATEGORY_LABELS[cat]}</div>
              <div className="space-y-1">
                {(items ?? []).map((it) => (
                  <div
                    key={it.item_key}
                    className="flex items-center justify-between border border-gray-100 rounded-lg px-3 py-2 text-sm"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      {it.auto_check ? (
                        it.done ? (
                          <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
                        ) : (
                          <Circle className="w-4 h-4 text-gray-300 flex-shrink-0" />
                        )
                      ) : (
                        <input
                          type="checkbox"
                          checked={it.done}
                          onChange={(e) => handleToggle(it.item_key, e.target.checked)}
                          disabled={markM.isPending}
                          className="w-4 h-4 flex-shrink-0"
                        />
                      )}
                      <div className="min-w-0">
                        <div className="truncate">
                          {it.label} {!it.required && <span className="text-gray-400 text-xs">(ไม่บังคับ)</span>}
                        </div>
                        {it.auto_check && (
                          <div className="text-[10px] text-gray-400">
                            {it.done ? 'ระบบเช็คให้แล้ว' : 'รอระบบเช็คให้อัตโนมัติ'}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </Modal>
  );
}
